/**
 * The single entry point to "Proteger reserva externa", wherever it is offered.
 *
 * It owns the modal and ALL of its Redux wiring, so a page only has to drop the
 * component in. That is the whole reason it exists: the reservations page and the
 * host dashboard both offer this action, and duplicating the modal plus its
 * mapState/mapDispatch into every page is how the two copies would drift apart.
 *
 * Three renderings of the SAME action — pick per surface, never two on one view:
 *   - `card`   the discovery panel for the reservations side nav (wide screens)
 *   - `banner` a compact tappable row: the card's message in one line, for narrow
 *              screens and for the dashboard
 *   - `button` a plain page action, for a page heading
 *
 * Responsive visibility is the CALLER's business (pass `className`): the same banner
 * is mobile-only on the reservations page but shown at every width on the dashboard.
 *
 * Renders nothing at all when the feature is unavailable — no database, or the user
 * is not a host. See docs/external-bookings.md.
 */

import React, { useState } from 'react';
import { oneOf, string } from 'prop-types';
import { compose } from 'redux';
import { connect } from 'react-redux';

import { MaterialSymbol, PrimaryButton } from '../../components';
import { manageDisableScrolling } from '../../ducks/ui.duck';
import {
  createExternalBookingThunk,
  externalBookingsEnabled,
  fetchHostListingsForExternalBooking,
  fetchListingTimeSlots,
} from '../../ducks/externalBookings.duck';
import { CUSTOMER } from '../../util/types';

import BetaPill from './BetaPill';
import ProtectExternalBookingModal from './ProtectExternalBookingModal/ProtectExternalBookingModal';
import css from './ExternalBookingEntry.module.css';

const TITLE = 'Protege tus reservas externas';
const SUBTITLE = 'Seguro incluido y cobro protegido.';
const ACTION = 'Proteger reserva externa';

export const ExternalBookingEntryComponent = props => {
  const {
    variant,
    className,
    id,
    available,
    userType,
    hostListings,
    listingTimeSlots,
    createInProgress,
    createError,
    onFetchHostListings,
    onFetchListingTimeSlots,
    onCreateExternalBooking,
    onManageDisableScrolling,
  } = props;

  const [isOpen, setIsOpen] = useState(false);
  // The offer just created, for the confirmation step. Local, not a store field: one
  // that survived the modal closing would re-open the confirmation on the next visit.
  const [created, setCreated] = useState(null);

  if (!available || userType === CUSTOMER) {
    return null;
  }

  const open = () => {
    // The pool picker needs the host's listings and their guest tiers; fetched
    // lazily, on open, so a page that never opens the modal never pays for it.
    onFetchHostListings();
    setIsOpen(true);
  };

  const close = () => {
    setIsOpen(false);
    setCreated(null);
  };

  const handleSubmit = values => {
    onCreateExternalBooking(values).then(result => {
      // Kept open on failure so the host doesn't lose what they typed.
      if (!result) {
        return;
      }
      // Always confirm rather than just closing: the guest's email is optional, so the
      // host is frequently the one who has to deliver the link, and even when it was
      // emailed they may want to send it over WhatsApp too.
      setCreated(result);
    });
  };

  const trigger =
    variant === 'button' ? (
      <button type="button" className={`${css.headerButton} ${className || ''}`} onClick={open}>
        {ACTION}
        <BetaPill className={css.headerButtonBeta} />
      </button>
    ) : variant === 'banner' ? (
      <button type="button" className={`${css.banner} ${className || ''}`} onClick={open}>
        <span className={css.bannerIcon}>
          <MaterialSymbol name="shield" size={24} />
        </span>
        <span className={css.bannerText}>
          <span className={css.bannerTitle}>
            {TITLE}
            <BetaPill className={css.betaPill} />
          </span>
          <span className={css.bannerBody}>{SUBTITLE}</span>
        </span>
        <MaterialSymbol name="chevron_right" size={24} className={css.bannerChevron} />
      </button>
    ) : (
      <div className={`${css.card} ${className || ''}`}>
        <span className={css.cardIcon}>
          <MaterialSymbol name="shield" size={32} />
        </span>
        {/* Wrapped, not just margined: the pill is inline-flex and the card centres by
            text-align, so on its own it sat on the icon's line instead of under it. */}
        <div className={css.cardBeta}>
          <BetaPill />
        </div>
        <h3 className={css.cardTitle}>{TITLE} con Cocopool</h3>
        <p className={css.cardBody}>{SUBTITLE}</p>
        <PrimaryButton className={css.cardButton} onClick={open}>
          {ACTION}
        </PrimaryButton>
      </div>
    );

  return (
    <>
      {trigger}
      <ProtectExternalBookingModal
        id={`${id || 'ExternalBookingEntry'}.modal`}
        isOpen={isOpen}
        onClose={close}
        onSubmit={handleSubmit}
        createdBooking={created?.booking}
        paymentLinkSent={!!created?.paymentLinkSent}
        onManageDisableScrolling={onManageDisableScrolling}
        inProgress={createInProgress}
        createError={createError}
        hostListings={hostListings}
        listingTimeSlots={listingTimeSlots}
        onFetchListingTimeSlots={onFetchListingTimeSlots}
      />
    </>
  );
};

ExternalBookingEntryComponent.defaultProps = { variant: 'banner' };
ExternalBookingEntryComponent.propTypes = {
  variant: oneOf(['card', 'banner', 'button']),
  className: string,
  id: string,
};

const mapStateToProps = state => ({
  available: externalBookingsEnabled(state),
  userType: state.user.currentUser?.attributes?.profile?.publicData?.userType,
  hostListings: state.externalBookings.hostListings,
  listingTimeSlots: state.externalBookings.listingTimeSlots,
  createInProgress: state.externalBookings.createInProgress,
  createError: state.externalBookings.createError,
});

const mapDispatchToProps = dispatch => ({
  onFetchHostListings: () => dispatch(fetchHostListingsForExternalBooking()),
  onFetchListingTimeSlots: (listingId, start, end, timeZone) =>
    dispatch(fetchListingTimeSlots(listingId, start, end, timeZone)),
  onCreateExternalBooking: values => dispatch(createExternalBookingThunk(values)),
  onManageDisableScrolling: (componentId, disableScrolling) =>
    dispatch(manageDisableScrolling(componentId, disableScrolling)),
});

export default compose(connect(mapStateToProps, mapDispatchToProps))(ExternalBookingEntryComponent);
